import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getAdvanceLeaves, updateLeaveStatus } from '../../../Redux/Slice/LeaveForm';
import { FiCalendar, FiMapPin, FiInfo, FiUsers, FiClock, FiAlertCircle, FiCheckCircle, FiXCircle, FiGrid, FiTrendingUp, FiLayers, FiActivity, FiSearch, FiChevronDown, FiFilter } from 'react-icons/fi';
import { MdOutlineMoreTime } from "react-icons/md";
import { BsGraphUpArrow } from "react-icons/bs";
import { LuMessageSquare } from "react-icons/lu";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format, isSameDay, addDays, getDay } from 'date-fns';
import { fetchAllMembers } from '../../../Redux/Slice/AddMember';
import { TablePagination } from "@mui/material";
import socket from '../../../socket';

const PMRequest = () => {
    const dispatch = useDispatch();
    const loginDataStr = Cookies.get("loginData");
    let loginData = null;
    if (loginDataStr) {
        loginData = JSON.parse(loginDataStr);
    }
    const applicantUser = loginData?.data || loginData?.user || {};

    const { leaveData, listLoading, error } = useSelector((state) => state.LeaveForm);
    const { MemberData } = useSelector((state) => state.AddMemberSlice);
    const [filter, setFilter] = useState('All'); 
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {

        socket.on("leaveUpdate", (data) => {
            console.log("Live leave update received (PM):", data);
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        });

        return () => {
            socket.off("leaveUpdate");
        };
    }, [dispatch, applicantUser?._id]);

    const role = applicantUser.Role;

    const [actionModal, setActionModal] = useState({ isOpen: false, id: null, actionType: null });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        if (applicantUser?._id) {
            dispatch(getAdvanceLeaves(applicantUser._id));
        }
        dispatch(fetchAllMembers());
    }, [dispatch, applicantUser?._id]);

    const handleAction = (id, action) => {
        setActionModal({ isOpen: true, id, actionType: action });
    };

    const confirmAction = async () => {
        const { id, actionType } = actionModal;
        if (id && actionType) {
            await dispatch(updateLeaveStatus({ id, action: actionType }));
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        }
        setActionModal({ isOpen: false, id: null, actionType: null });
    };

    const cancelAction = () => {
        setActionModal({ isOpen: false, id: null, actionType: null });
    };

    const processedLeaves = leaveData?.map(leave => {
        const userApprover = leave.approvers?.find(app => String(app.approverId) === String(applicantUser?._id));
        const userStatus = userApprover ? userApprover.status : leave.Status;

        const isCurrentApprover = leave.approvers &&
            leave.currentApprovalStep < leave.approvers.length &&
            String(leave.approvers[leave.currentApprovalStep].approverId) === String(applicantUser?._id);

        return {
            ...leave,
            userStatus,
            isCurrentApprover
        };
    }) || [];

    const filteredLeaves = processedLeaves.filter(leave => {
        if (leave.Status === 'Withdrawn') return false;
        const myApproverData = leave.approvers?.find(app => String(app.approverId) === String(applicantUser?._id));

        if (myApproverData) {
            if (myApproverData.status === 'Pending' && !leave.isCurrentApprover) {
                return false;
            }
        }

        const matchesSearch = 
            (leave.applicantName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (leave.reason || '').toLowerCase().includes(searchQuery.toLowerCase());

        if (!matchesSearch) return false;

        if (filter === 'All') return true;
        if (filter === 'Approved' && leave.userStatus === 'Approved by Owner') return true;
        return leave.userStatus === filter;
    });

    let teamCount = 0;
    const membersArray = MemberData?.data || (Array.isArray(MemberData) ? MemberData : []);

    if (Array.isArray(membersArray)) {
        if (role === 'Web Senior Developer') {
            teamCount = membersArray.filter(member => member.Role === 'Web Junior Developer' && member.Profile === 'On').length;
        } else if (role === 'Delphi Senior Developer') {
            teamCount = membersArray.filter(member => member.Role === 'Delphi Junior Developer' && member.Profile === 'On').length;
        } else {
            teamCount = membersArray.length;
        }
    }

    let allCount = leaveData?.filter(l => l.Status === 'Approved').length;
    const teamAvailability = teamCount > 0
        ? Math.round(((teamCount - allCount) / teamCount) * 100)
        : 100;

    const leaveDistribution = (leaveData || [])
        .filter(l => l.Status === 'Approved')
        .reduce((acc, leave) => {
            const type = leave.LeaveType;
            if (type) acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

    const chartData = [
        { name: "Advance Leave", count: leaveDistribution["Advance Leave"] || 0, color: "#133A69" },
        { name: "Emergency Leave", count: leaveDistribution["Emergency Leave"] || 0, color: "#EF4444" },
        { name: "Late Arrival", count: leaveDistribution["Late Arrival"] || 0, color: "#8b5cf6" },
        { name: "Early Leave", count: leaveDistribution["Early Leave"] || 0, color: "#f97316" },
        { name: "Half Day Leave", count: leaveDistribution["Half Day Leave"] || 0, color: "#14b8a6" },
        { name: "Time Break", count: leaveDistribution["Time Break"] || 0, color: "#3B82F6" }
    ];

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Pending':
                return 'bg-orange-50 text-orange-600 border-orange-100';
            case 'Approved':
            case 'Approved by Owner':
                return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'Rejected':
                return 'bg-rose-50 text-rose-600 border-rose-100';
            default:
                return 'bg-gray-50 text-gray-400 border-gray-100';
        }
    };

    const calculateWorkingDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        let count = 0;
        let curr = new Date(startDate);
        const end = new Date(endDate);
        while (curr <= end) {
            if (getDay(curr) !== 0) { 
                count++;
            }
            curr = addDays(curr, 1);
        }
        return count;
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-6 rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-100 flex flex-col items-center gap-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Category Insight</p>
                    <div className="w-10 h-1 bg-gray-50 rounded-full my-1"></div>
                    <p className="text-[15px] font-black text-[#133A69]">{data.name}</p>
                    <p className="text-[12px] font-black text-gray-500">{data.count} Approved Requests</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="px-3 py-6 w-full min-h-0 space-y-6">
            
            {/* Premium Dashboard Header */}
            <div className="bg-white p-5 md:p-6 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 relative overflow-hidden mb-4">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-full blur-3xl -mr-64 -mt-64 opacity-60 pointer-events-none"></div>
                <div className="flex items-center gap-5 relative z-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#133A69] to-blue-800 rounded-xl flex items-center justify-center text-white shadow-xl shadow-blue-900/20 border border-blue-700/50 transform transition-transform hover:scale-105">
                        <LuMessageSquare size={22} className="md:w-6 md:h-6" />
                    </div>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">Leave Operations Desk</h1>
                        <p className="text-gray-650 text-xs font-semibold mt-1 uppercase tracking-widest leading-relaxed">Systematic Application Review</p>
                    </div>
                </div>

                <div className="flex bg-gray-50/80 backdrop-blur-md rounded-xl p-1.5 border border-gray-100 relative z-10 w-full xl:w-auto overflow-x-auto custom-scrollbar">
                    {['All', 'Pending', 'Approved', 'Rejected'].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-5 py-2.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-1 xl:flex-none ${filter === s
                                ? 'bg-white text-[#133A69] shadow-sm border border-gray-100'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-250/50'}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Top Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                
                {/* Visual Stats (Chart) */}
                <div className="lg:col-span-8">
                    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100 p-6 relative overflow-hidden group h-full flex flex-col justify-between">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-2xl -mr-32 -mt-32 transition-transform duration-700 group-hover:scale-110 pointer-events-none"></div>
                        
                        <div className="relative z-10 flex flex-row justify-between items-center mb-6 gap-3 w-full">
                            <div className="flex flex-col overflow-hidden">
                                <h3 className="text-base md:text-lg font-bold text-gray-900 tracking-tight truncate">Analytics Overview</h3>
                                <p className="text-xs text-gray-600 font-semibold uppercase tracking-widest mt-1 truncate">Confirmed absences</p>
                            </div>
                            <div className="p-2.5 md:p-3 bg-blue-50 rounded-xl border border-blue-100 text-[#133A69] shadow-inner shrink-0">
                                <BsGraphUpArrow className="w-4 h-4 md:w-5 md:h-5" />
                            </div>
                        </div>

                        <div className="w-full h-[280px] md:h-[300px] relative z-10 -ml-2 md:-ml-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    layout="vertical"
                                    data={chartData}
                                    margin={{ top: 0, right: 10, left: -10, bottom: 0 }}
                                    barSize={20}
                                >
                                    <CartesianGrid strokeDasharray="8 8" horizontal={false} stroke="#f1f5f9" />
                                    <XAxis type="number" hide />
                                    <YAxis 
                                        dataKey="name" 
                                        type="category" 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 800 }} 
                                        width={110} 
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(241, 245, 249, 0.6)' }}
                                        content={<CustomTooltip />}
                                    />
                                    <Bar dataKey="count" radius={[0, 12, 12, 0]}>
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Counter Cards */}
                <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
                    <div className="bg-white rounded-2xl p-5 md:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-150 flex items-center justify-between group hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 overflow-hidden pr-2">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1 truncate">Pending Approval</p>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tighter">
                                {processedLeaves?.filter(l => l.userStatus === 'Pending' && l.isCurrentApprover).length || 0}
                            </h2>
                        </div>
                        <div className="w-12 h-12 shrink-0 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shadow-inner border border-orange-100 relative z-10 group-hover:scale-105 transition-transform">
                            <FiClock className="w-5 h-5" />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-5 md:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-150 flex items-center justify-between group hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 overflow-hidden pr-2">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1 truncate">Availability Rate</p>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tighter">{teamAvailability}%</h2>
                        </div>
                        <div className="w-12 h-12 shrink-0 rounded-xl bg-blue-50 text-[#133A69] flex items-center justify-center shadow-inner border border-blue-100 relative z-10 group-hover:scale-105 transition-transform">
                            <FiUsers className="w-5 h-5" />
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#133A69] to-blue-900 rounded-2xl p-5 md:p-6 shadow-xl shadow-blue-900/30 flex items-center justify-between group hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden border border-blue-800">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-xl -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700"></div>
                        <div className="relative z-10 overflow-hidden pr-2">
                            <p className="text-xs font-bold text-blue-200 uppercase tracking-widest mb-1 truncate">On Rotation</p>
                            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tighter">{allCount} <span className="text-xs text-blue-300 font-semibold tracking-normal">Away</span></h2>
                        </div>
                        <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 text-white flex items-center justify-center border border-white/20 relative z-10 backdrop-blur-sm group-hover:scale-105 transition-transform">
                            <FiLayers className="w-5 h-5" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Search & Bulk View Section */}
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-center bg-white rounded-[24px] p-3 md:p-4 md:px-6 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] gap-4">
                    <div className="relative w-full sm:max-w-md">
                        <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Find application by employee name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-6 py-3.5 bg-gray-50 border border-gray-100 rounded-[20px] outline-none focus:bg-white focus:border-[#133A69] focus:ring-4 focus:ring-blue-50 text-[14px] font-bold text-gray-800 transition-all placeholder:text-gray-400"
                        />
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 px-5 py-3 rounded-[20px] border border-gray-100 text-gray-600 text-[12px] font-black uppercase tracking-widest">
                        <FiFilter size={16} className="text-[#133A69]" />
                        {filteredLeaves.length} Records Visualized
                    </div>
                </div>

                {listLoading ? (
                    <div className="py-24 flex flex-col items-center gap-6">
                        <div className="w-12 h-12 border-4 border-[#133A69]/10 border-t-[#133A69] rounded-full animate-spin shadow-inner"></div>
                        <p className="text-[13px] font-black text-gray-400 uppercase tracking-[0.2em]">Synchronizing Records...</p>
                    </div>
                ) : filteredLeaves.length === 0 ? (
                    <div className="py-24 bg-white rounded-[40px] border-2 border-dashed border-gray-100 flex flex-col items-center text-center">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <FiGrid className="text-gray-200" size={40} />
                        </div>
                        <h3 className="text-2xl font-black text-gray-800 tracking-tight">Queue is Clear</h3>
                        <p className="text-gray-400 font-medium max-w-[300px] mt-2 leading-relaxed">No applications found matching your criteria in the system right now.</p>
                    </div>
                ) : (
                    <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 relative z-0">
                        <div className="w-full overflow-x-auto" style={{ width: "100%" }}>
                            <div className="min-w-[1000px] overflow-hidden relative z-0">
                                <div className="max-h-[600px] overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                    <table className="min-w-full table-auto border-collapse divide-y divide-gray-200 text-sm">
                                        <thead className="bg-[#133A69] text-white sticky top-0 z-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "200px" }}>Employee</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>Leave Type</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "180px" }}>Duration</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "250px" }}>Reason Details</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>Status</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "180px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {filteredLeaves.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((leave) => (
                                                <tr key={leave._id} className="group transition-all duration-200 hover:shadow-md hover:bg-gray-50">
                                                    <td className="px-6 py-4 align-top">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-10 h-10 rounded-full bg-blue-50 border border-gray-100 overflow-hidden shrink-0">
                                                                <img className='w-full h-full object-cover shadow-sm' src={leave?.applicant?.ProfilePhoto} alt={leave?.applicantName} />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-[13px] font-bold text-gray-800 leading-tight group-hover:text-[#133A69] transition-colors">{leave.applicantName || "User"}</h4>
                                                                <p className="text-[11px] font-semibold text-gray-500 mt-0.5">{leave.applicantRole}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 align-top">
                                                        <div className="flex flex-col items-start gap-1">
                                                            <span className="px-2 py-1 rounded bg-gray-50 text-[12px] font-semibold text-gray-700 border border-gray-200">
                                                                {leave.LeaveType}
                                                            </span>
                                                            {leave.LeaveType === 'Emergency Leave' && leave.leaveCategory && (
                                                                <span className="text-[11px] font-bold text-red-500 ml-1 mt-1 block">
                                                                    ({leave.leaveCategory})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 align-top">
                                                        <div className="flex flex-col gap-1.5">
                                                            <div className="flex items-center gap-2 text-[12.5px] font-semibold text-gray-700">
                                                                <FiCalendar className="text-gray-400" size={13} />
                                                                {isSameDay(new Date(leave.fromDate), new Date(leave.toDate))
                                                                    ? format(new Date(leave.fromDate), 'dd MMM yyyy')
                                                                    : `${format(new Date(leave.fromDate), 'dd MMM')} - ${format(new Date(leave.toDate), 'dd MMM')}`}
                                                            </div>
                                                            <div className="text-[11.5px] font-bold text-blue-600 bg-blue-50/50 px-2 py-0.5 rounded inline-block w-fit">
                                                                {leave.LeaveType === "Early Leave" ? `Leaving at: ${leave.leavingTime}` : 
                                                                 leave.LeaveType === "Late Arrival" ? `Arriving at: ${leave.arrivalTime}` :
                                                                 leave.LeaveType === "Time Break" ? `${leave.leavingTime} - ${leave.arrivalTime}` :
                                                                 `${leave.TotalDays || calculateWorkingDays(leave.fromDate, leave.toDate)} Days`}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 align-top">
                                                        <div className="flex flex-col gap-2">
                                                            <div className="text-[13px] text-gray-600 font-medium leading-relaxed whitespace-pre-wrap bg-gray-50/50 p-2.5 rounded-lg border border-gray-100 min-h-[40px]" title={leave.reason}>
                                                                {leave.reason || "-"}
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-blue-500 mt-0.5">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                                                Step {leave.currentApprovalStep} of {leave.approvers?.length || 0}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 align-top whitespace-nowrap text-center">
                                                        <span className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold tracking-wide border shadow-sm ${getStatusStyle(leave.userStatus)}`}>
                                                            {leave.userStatus}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 align-top whitespace-nowrap text-center">
                                                        {leave.userStatus === 'Pending' && leave.isCurrentApprover ? (
                                                            <div className="flex items-center justify-center gap-2 mt-1">
                                                                <button
                                                                    onClick={() => handleAction(leave._id, 'Reject')}
                                                                    className="px-3 py-1.5 rounded bg-red-50 text-red-600 border border-red-200 text-[12px] font-semibold hover:bg-red-500 hover:text-white transition-all flex items-center gap-1.5 shadow-sm"
                                                                >
                                                                    <FiXCircle size={14} />
                                                                    Reject
                                                                </button>
                                                                <button
                                                                    onClick={() => handleAction(leave._id, 'Approve')}
                                                                    className="px-3 py-1.5 rounded bg-green-50 text-green-600 border border-green-200 text-[12px] font-semibold hover:bg-green-600 hover:text-white transition-all flex items-center gap-1.5 shadow-sm"
                                                                >
                                                                    <FiCheckCircle size={14} />
                                                                    Approve
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex justify-center mt-1">
                                                                <span className={`px-4 py-1.5 rounded border text-[12px] font-semibold shadow-sm ${leave.userStatus === 'Approved by Owner' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                                    {leave.userStatus === 'Withdrawn' ? 'Withdrawn' : (leave.userStatus === 'Approved by Owner' ? 'Approved by Owner' : 'Processed')}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <TablePagination
                            component="div"
                            count={filteredLeaves.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className="bg-white border-t border-gray-100 placeholder:text-gray-500 rounded-[0_0_16px_16px]"
                        />
                    </div>
                )}
            </div>

            {/* Premium Action Modal */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 bg-[#0f172a]/70 z-[100] flex items-center justify-center p-4 backdrop-blur-md transition-all duration-500">
                    <div className="bg-white rounded-[40px] p-10 pb-8 max-w-[420px] w-full shadow-[0_40px_100px_rgba(0,0,0,0.4)] flex flex-col items-center text-center relative overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className={`absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16 ${actionModal.actionType === 'Approve' ? 'bg-emerald-50' : 'bg-rose-50'}`}></div>
                        
                        <div className={`w-[88px] h-[88px] rounded-3xl flex items-center justify-center mb-8 shadow-inner border-2 relative z-10 ${actionModal.actionType === 'Approve' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                            {actionModal.actionType === 'Approve' ? 
                                <FiCheckCircle className="text-emerald-500 w-10 h-10 stroke-[2.5]" /> : 
                                <FiXCircle className="text-rose-500 w-10 h-10 stroke-[2.5]" />
                            }
                        </div>

                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tighter relative z-10">
                            Confirm Decision?
                        </h3>

                        <p className="text-[15px] font-semibold text-gray-500 leading-relaxed mb-10 px-4 relative z-10">
                            Are you sure you want to <strong className={`${actionModal.actionType === 'Approve' ? 'text-emerald-600' : 'text-rose-600'}`}>{actionModal.actionType?.toLowerCase()}</strong> this application? This action will recorded in the system.
                        </p>

                        <div className="w-full flex flex-col gap-3 relative z-10">
                            <button
                                className={`w-full text-white text-[15px] font-black py-4 px-6 rounded-2xl shadow-xl transition-all active:scale-95 ${actionModal.actionType === 'Approve' ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-900/20' : 'bg-rose-500 hover:bg-rose-600 shadow-rose-900/20'}`}
                                onClick={confirmAction}
                            >
                                Process {actionModal.actionType}
                            </button>

                            <button
                                onClick={cancelAction}
                                className="w-full py-4 text-gray-400 font-bold text-[14px] hover:text-gray-800 transition-colors"
                            >
                                Not now, review again
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PMRequest;
